/**
 * The rules for publishing to a marketplace from the seller's own browser.
 *
 * Poshmark and Depop have no server-to-server create API, so their posts are made by the Chrome
 * extension in the seller's browser. That leaves a set of questions only this app can answer, and
 * they all belong together because they all describe the same thing from different angles: does a
 * post still need making, is one being made right now, what did an attempt mean, and what gets
 * recorded about it.
 *
 * Kept apart from `extension-publish.js`, which performs the round trip, and deliberately so: that
 * module pulls in the app's http client and firebase with it, so nothing in it can be exercised
 * without standing up mocks for both. Everything here is a pure function of data it is handed,
 * which is what makes the reasoning testable at all. Nothing in this file may import the http
 * client, and nothing that does may move into it.
 */

import { ANALYTICS_EVENTS } from 'src/utils/posthog';
import { getPlatformDisplayName } from 'src/utils/platform-label';

// --------------------------------------------------------------------------------------
// Reading one create result
// --------------------------------------------------------------------------------------

/**
 * The reasons a post can be live on the marketplace while Voolist has no record of it. Each is a
 * flag a create result may carry:
 *
 *   `linkedToAnotherListing` the extension made the post, and Voolist already holds it as another of
 *                            the seller's listings, so this one is not linked to it. The seller
 *                            merges the two. Sent together with `unrecorded`, which web apps that
 *                            predate it read the same safe way.
 *   `unrecorded`             the extension made the post and could not save it to Voolist. The
 *                            post is definitely live; our record of it is what is missing.
 *   `marketplaceUnconfirmed` the marketplace was asked to make the post and never said whether it
 *                            did: it failed with a server error, or its answer never arrived.
 *   `timedOut`               the extension went quiet before it said what happened, so nobody
 *                            knows whether the post was made.
 *
 * Listed from the most certain to the least, which is the order a result is read in.
 */
const UNCONFIRMED_CREATE_REASONS = Object.freeze(['linkedToAnotherListing', 'unrecorded', 'marketplaceUnconfirmed', 'timedOut']);

/**
 * Could a post be live on the marketplace that Voolist has no record of?
 *
 * Every reason above means the same three things, which is why they share one answer: the outcome
 * must never be reported to the server as a failure (that clears the claim which stops the post
 * being made twice), it must never be retried on the seller's behalf, and it must never let a run
 * read as cleanly finished. Telling the seller what happened is the only place they differ.
 *
 * @param {{timedOut?: boolean, unrecorded?: boolean, marketplaceUnconfirmed?: boolean, linkedToAnotherListing?: boolean}|null} result
 *   - a create result
 * @returns {boolean}
 */
export function isUnconfirmedCreate(result) {
  return unconfirmedCreateReason(result) !== null;
}

/**
 * Which reason a create is unconfirmed for, so each screen can tell the seller what happened.
 *
 * @param {Object|null} result - a create result
 * @returns {'linkedToAnotherListing'|'unrecorded'|'marketplaceUnconfirmed'|'timedOut'|null} null when it is not unconfirmed
 */
export function unconfirmedCreateReason(result) {
  return UNCONFIRMED_CREATE_REASONS.find((reason) => Boolean(result?.[reason])) || null;
}

/**
 * Every unconfirmed-create flag of a result, as booleans. This is what each layer passes a result
 * on with, so no layer can carry some of the reasons and silently drop another — a dropped one
 * turns a create that may be live into a plain failure, which is reported and offered again.
 *
 * @param {Object|null} result - a create result
 * @returns {{linkedToAnotherListing: boolean, unrecorded: boolean, marketplaceUnconfirmed: boolean, timedOut: boolean}}
 */
export function unconfirmedCreateFlags(result) {
  return Object.fromEntries(UNCONFIRMED_CREATE_REASONS.map((reason) => [reason, Boolean(result?.[reason])]));
}

// --------------------------------------------------------------------------------------
// Which marketplaces still owe a post
// --------------------------------------------------------------------------------------

// Marketplaces whose posts are created through the extension. Matches the server's
// EXTENSION_BASED_PLATFORMS (marketplaces.js, `extensionBased`).
export const EXTENSION_CREATE_PLATFORMS = ['poshmark', 'depop', 'grailed', 'whatnot', 'offerup', 'vestiaire'];

// Marketplaces whose posts only the seller's browser can take down. Switching one off records the
// takedown and leaves the post where it is until the extension performs it and reports back, so
// the item stays live until then and the words shown to the seller have to say so.
//
// Poshmark is deliberately absent: the server can delete a Poshmark post itself, so switching it
// off removes it there and then. Matches the server's isExtensionOnlyPlatform.
export const BROWSER_ONLY_TAKEDOWN_PLATFORMS = ['depop', 'vinted', 'mercari', 'grailed', 'whatnot', 'offerup', 'vestiaire'];

/** Can only the seller's browser take this marketplace's post down? */
export function needsBrowserToTakeDown(platform) {
  return BROWSER_ONLY_TAKEDOWN_PLATFORMS.includes(platform);
}

// Marketplaces whose takedown hides a post instead of deleting it: the post stays on the marketplace,
// unbuyable and still linked to the listing, and switching the marketplace back on shows it again — so
// the words shown to the seller say "hidden", never "removed". Each says it in its sellers' own words:
// `hide` (the action) and `hidden` (the state it leaves the listing in), as the server's `hidingWords`
// (marketplaces.js) say, `hiding` (doing it), and `keptIn`: where the listing waits on the marketplace
// meanwhile. Matches the server's `takedownHidesPost`.
const HIDING_WORDS = Object.freeze({
  whatnot: Object.freeze({
    hide: 'hide', hiding: 'hiding', hidden: 'hidden', keptIn: (marketplace) => `your ${marketplace} inventory`,
  }),
  offerup: Object.freeze({
    hide: 'archive', hiding: 'archiving', hidden: 'archived', keptIn: (marketplace) => `your archived items on ${marketplace}`,
  }),
});

export const TAKEDOWN_HIDES_POST_PLATFORMS = Object.keys(HIDING_WORDS);

/** Does taking this marketplace's post down hide it rather than delete it? */
export function takedownHidesPost(platform) {
  return TAKEDOWN_HIDES_POST_PLATFORMS.includes(platform);
}

/**
 * The words sellers use on this marketplace for a takedown that hides the post (HIDING_WORDS), or null for
 * a marketplace whose takedown deletes it.
 *
 * @param {string} platform
 * @returns {({hide: string, hiding: string, hidden: string, keptIn: (marketplace: string) => string}|null)}
 */
export function hidingWordsOf(platform) {
  return Object.prototype.hasOwnProperty.call(HIDING_WORDS, platform) ? HIDING_WORDS[platform] : null;
}

/**
 * Is this marketplace switched off while its post waits for the browser to take it down?
 *
 * The post stays up until the extension removes it, so the listing still counts as listed there. But
 * the seller's choice is "off", and the edit form has to show that: shown switched on, the
 * marketplace is sent back as wanted, and the save takes the removal back. The API marks such an
 * account `takedownWaiting`.
 *
 * Only while the item is in stock: a takedown waiting on a sold-out item is taken as the sale's, not
 * the seller's choice, and restocking the item withdraws it.
 *
 * @param {Object|null} listing - a saved listing as the API serves it
 * @param {string} platform
 * @returns {boolean}
 */
export function isTakedownWaiting(listing, platform) {
  if (!listing || !needsBrowserToTakeDown(platform) || !(Number(listing.quantity) > 0)) return false;
  return (listing.platformAccounts || []).some((account) => account.platform === platform && account.takedownWaiting === true);
}

// The server marks a create 'creating' while the extension works on it and lets anyone re-claim it
// once its claim's lifetime has passed, on the assumption the previous attempt died with its tab. Most
// marketplaces' claims last this long; a create queued and never claimed is given this long too before
// it reads as nothing happening.
export const STALE_CLAIM_MS = 2 * 60 * 1000;

// A marketplace whose post takes longer to make: a Vestiaire listing is made in a dozen of Vestiaire's
// own steps, and its create stops once seven minutes have passed.
const CLAIM_LIFETIME_BY_PLATFORM = Object.freeze({ vestiaire: 10 * 60 * 1000 });

/**
 * How long a browser's claim on a marketplace's new post stays its own before it may be taken over. The
 * server enforces the same lifetimes (claimLifetimeMs in listing-platform-account.repository.js): the two
 * must move together, or the browser and the server disagree about who owns a create.
 *
 * @param {string} platform
 * @returns {number} ms
 */
export const claimLifetimeMs = (platform) => CLAIM_LIFETIME_BY_PLATFORM[platform] ?? STALE_CLAIM_MS;

// What a placement shows while a browser makes its new post: a create, or a relist — posting again on
// Grailed an item that sold there (the server's POSTING_WORK).
const POSTING_CLAIMS = ['creating', 'relisting'];

/**
 * @param {Object|null} listing - a saved listing as the API serves it
 * @param {Object} [options]
 * @param {number} [options.now] - current time, injectable for tests
 * @param {number} [options.staleClaimMs] - how old a claim must be before it may be taken over; by default
 *   each marketplace's claim lifetime (claimLifetimeMs)
 * @returns {string[]} platforms this browser should create now
 */
export function pendingExtensionCreates(listing, { now = Date.now(), staleClaimMs } = {}) {
  if (!listing) return [];

  return EXTENSION_CREATE_PLATFORMS.filter((platform) => {
    // The post already exists — whatever the queue says, creating again would duplicate it.
    if (listing[`${platform}ListingId`]) return false;

    const aspects = listing[`${platform}Aspects`];

    // `pendingSync` is what says work is still owed. It is the only field that is cleared when a
    // create finishes or is given up on — the ACTION is left behind either way, so reading the
    // action alone would re-run a create that has already failed, over and over.
    if (!aspects?.pendingSync) return false;

    // A create that failed waits for the seller: the marketplace rejected something about the
    // listing, so sending the same data again fails the same way.
    if (aspects.pendingSyncError) return false;

    const action = aspects.pendingSyncAction;

    // Nobody has picked this up yet.
    if (action === 'create') return true;

    // Someone claimed it. Theirs until the claim goes stale, at which point the attempt is assumed
    // to have died with its tab and may be taken over.
    if (action === 'creating') {
      const claimedAt = Number(aspects?.pendingSyncClaimedAt) || 0;
      return now - claimedAt >= (staleClaimMs ?? claimLifetimeMs(platform));
    }

    // No queued create — including a takedown, an edit, or a create that already failed and is
    // waiting for the seller to change something. None of those are ours to run.
    return false;
  });
}

/**
 * Could this save have left work for the EXTENSION'S OWN QUEUE to run?
 *
 * Two different things run a save's extension work and only one was being started. Creates are
 * driven from the listing page (`pendingExtensionCreates` above, published one at a time while the
 * seller waits). An edit to a post that already exists is not: it is queued on the placement and run
 * by the extension's queue, which does nothing until something asks it to. Nothing asked it after a
 * single-listing save, so a seller who changed a listing already live on Poshmark or Depop saw
 * nothing happen at all and the edit waited for a browser restart. Bulk edit had always asked.
 *
 * This asks "could there be work", not "is there work", and that is deliberate. The saved listing
 * cannot answer the second question: `getListingById` surfaces the pending-sync flag only for a
 * create-pending draft and deliberately leaves a LIVE placement's flag out, because the extension
 * polls those separately. So a queued edit is invisible here by design, and a detector built on that
 * flag would never fire — which is exactly the bug this replaced.
 *
 * Being live on the marketplace is the honest signal, because that is precisely when an edit can
 * have been queued. Waking the queue with nothing to do is cheap and harmless: it answers "no
 * pending listings" and stops. What to actually run stays the extension's decision.
 *
 * @param {Object|null} listing - a saved listing as the API serves it
 * @returns {string[]} the marketplaces whose queue should be asked to run, for naming them
 *   while it happens — the seller sees "Updating on Depop" the same way a create names itself
 */
export function queuedExtensionSyncPlatforms(listing) {
  if (!listing) return [];

  return EXTENSION_CREATE_PLATFORMS.filter((platform) => (
    Boolean(listing[`${platform}ListingId`]) || Boolean(listing.listedOn?.includes(platform))
  ));
}

// --------------------------------------------------------------------------------------
// Where a marketplace publish stands
// --------------------------------------------------------------------------------------

/**
 * Where a marketplace publish stands, for a listing the API has served.
 *
 * A publish can be waiting, in flight, or stopped by something the marketplace refused. None of
 * that is visible from the listing's own fields — it lives on the placement, which the API
 * surfaces under `{platform}Aspects`. Without this the marketplace simply reads as "not listed",
 * which is how a seller can wait days for a post that was never going to appear.
 */

/**
 * @param {Object|null} listing - a saved listing as the API serves it
 * @param {string} platform
 * @param {Object} [options]
 * @param {number} [options.now] - current time, injectable for tests
 * @param {number} [options.staleClaimMs] - how long a claim may sit before its browser is presumed gone, and a
 *   queued create before it reads as nothing happening; by default the marketplace's claim lifetime
 *   (claimLifetimeMs) and STALE_CLAIM_MS
 * @returns {{state: 'publishing'|'stalled'|'failed', error: string|null}|null} null when there is
 *   nothing to say: the item is live there, or that marketplace was never asked for.
 */
export function extensionPublishStatus(listing, platform, { now = Date.now(), staleClaimMs } = {}) {
  if (!listing || !EXTENSION_CREATE_PLATFORMS.includes(platform)) return null;

  // A post exists — whatever the queue once said, this marketplace is done.
  if (listing[`${platform}ListingId`]) return null;

  const aspects = listing[`${platform}Aspects`];
  if (!aspects) return null;

  // Checked before the pending flag: a failure CLEARS that flag (failPendingSync) while recording
  // the error, so reading the flag first would report "nothing happening" for the one state the
  // seller most needs to see.
  if (aspects.pendingSyncError) {
    return { state: 'failed', error: aspects.pendingSyncError };
  }

  if (aspects.pendingSync) {
    // A create that ran out of time is deliberately never reported to the server as failed — the
    // post may have landed, and saying otherwise could produce a second one. So the claim is simply
    // abandoned, and without this the marketplace would read "publishing" for ever. Once the claim
    // is old enough that its browser is presumed gone, say plainly that it did not finish. A relist's
    // post is made the same way.
    const claimedAt = Number(aspects.pendingSyncClaimedAt) || 0;
    const claimAbandoned = POSTING_CLAIMS.includes(aspects.pendingSyncAction)
      && now - claimedAt >= (staleClaimMs ?? claimLifetimeMs(platform));
    if (claimAbandoned) return { state: 'stalled', error: null };

    // A create that was queued and never claimed is a different thing, and saying "publishing"
    // for it was a dead end: only the tab that queued it ever runs a create — recovery skips them
    // on purpose — so once that tab has moved on, nothing will ever pick it up. The marketplace
    // read "publishing" for ever and the seller could not even switch it off again, because the
    // rail shows this state instead of the toggle.
    //
    // It is NOT reported like an abandoned claim. Nothing was sent, so there is no post to go and
    // check for; the honest answer is that this marketplace simply has nothing happening, which
    // gives the seller their toggle back and lets a plain Save try again.
    const queuedAt = Number(aspects.pendingSyncQueuedAt) || 0;
    const neverStarted = aspects.pendingSyncAction === 'create' && queuedAt > 0
      && now - queuedAt >= (staleClaimMs ?? STALE_CLAIM_MS);
    if (neverStarted) return null;

    return { state: 'publishing', error: null };
  }

  return null;
}

/**
 * Every marketplace of this listing that is waiting, did not finish, or stopped.
 * @returns {Array<{platform: string, state: 'publishing'|'stalled'|'failed', error: string|null}>}
 */
export function extensionPublishStatuses(listing, options) {
  return EXTENSION_CREATE_PLATFORMS
    .map((platform) => {
      const status = extensionPublishStatus(listing, platform, options);
      return status ? { platform, ...status } : null;
    })
    .filter(Boolean);
}

// --------------------------------------------------------------------------------------
// Whether a bulk run still has to make a post
// --------------------------------------------------------------------------------------

// Whether one (listing, marketplace) of a bulk run still needs a post made, and what to show when
// it does not.
//
// A run can be started over the same batch more than once: the page is reloaded, or the seller
// comes back to it. Every one of those passes has to answer the same question, and answering it
// wrongly in the permissive direction posts a seller's item twice. So the reasoning lives here,
// free of the runner's reporting and progress side effects, where it can be tested on its own.


/**
 * @typedef {Object} PriorOutcome
 * @property {'alreadyListed'|'replay'|'create'} kind
 *   'alreadyListed' — nothing to do, and the row reads as already on the marketplace.
 *   'replay'        — an earlier pass already dealt with this; show what it concluded.
 *   'create'        — nobody has made this post, and nobody else is making it right now.
 * @property {Object} [result] - what to show, for 'alreadyListed' and 'replay'
 */

/**
 * What an earlier pass, the placement, or the marketplace already says about this post.
 *
 * The order of the checks is the whole point, and each one is answered before the next because it
 * is a stronger fact than the one after it:
 *
 *   1. **A success this batch's own log already holds.** This run made the post — or found nothing
 *      to make — and said so. The marketplace id the next check finds is that same post, so both
 *      answers mean "create nothing"; the log's is the one the seller was already shown ("Listed",
 *      not "Already listed").
 *   2. **The marketplace id on the placement.** A post exists. Nothing else any log says can
 *      outrank that, and a pass that ended not knowing where a create stood would otherwise keep
 *      repeating that answer long after the post had been linked.
 *   3. **This batch's own log of what it already attempted.** Its conclusion stands and the
 *      attempt is not repeated. An attempt that ended unable to say whether the post was made is
 *      precisely the one a second pass must not run again.
 *   4. **The placement's queued work.** Anything other than an unclaimed create is somebody
 *      else's job, or a create another tab claimed moments ago. Only a claim old enough that its
 *      browser is presumed gone is taken over.
 *
 * @param {Object} input
 * @param {Object} input.listing - the listing as the details endpoint serves it
 * @param {string} input.platform
 * @param {{listingId: string, aspects: string}} input.fields - where this platform's id and
 *   aspects live on the listing
 * @param {Object|undefined} input.previousAttempt - this batch's log entry for this pair, if any
 * @param {number} [input.now]
 * @param {number} [input.staleClaimMs] - by default the marketplace's claim lifetime (claimLifetimeMs)
 * @returns {PriorOutcome}
 */
export function priorCreateOutcome({
  listing,
  platform,
  fields,
  previousAttempt,
  now = Date.now(),
  staleClaimMs,
}) {
  const alreadyListed = { kind: 'alreadyListed', result: { success: true, alreadyCreated: true, platform } };

  if (previousAttempt?.result?.success) return { kind: 'replay', result: previousAttempt.result };

  if (fields && listing?.[fields.listingId]) return alreadyListed;

  if (previousAttempt) {
    return { kind: 'replay', result: previousAttempt.result || alreadyListed.result };
  }

  const aspects = fields ? listing?.[fields.aspects] : null;
  const action = aspects?.pendingSyncAction;

  // No queued action at all, or one nobody has picked up: this pass makes the post.
  if (!action || action === 'create') return { kind: 'create' };

  // Someone is making it right now. Theirs until the claim goes stale, at which point the attempt
  // is assumed to have died with its tab and may be taken over.
  if (action === 'creating') {
    const claimedAt = Number(aspects?.pendingSyncClaimedAt) || 0;
    return now - claimedAt >= (staleClaimMs ?? claimLifetimeMs(platform)) ? { kind: 'create' } : alreadyListed;
  }

  // A different job entirely, such as a queued takedown. Not this run's to touch.
  return alreadyListed;
}

// --------------------------------------------------------------------------------------
// A create held back for now
// --------------------------------------------------------------------------------------

// Why a marketplace's claim can hold a create back (Vestiaire's gate in the claim): the time it asks
// between two new listings, the most it is sent in a day, or Voolist having paused new listings there.
const HELD_BACK_REASONS = Object.freeze(['paceWait', 'capReached', 'marketplaceOff']);

/** A wait as a seller reads it, rounded up: "a minute", "2 minutes", "3 hours". */
function waitWords(seconds) {
  if (!(seconds > 60)) return 'a minute';
  if (seconds < 60 * 60) return `${Math.ceil(seconds / 60)} minutes`;
  const hours = Math.ceil(seconds / (60 * 60));
  return hours === 1 ? 'an hour' : `${hours} hours`;
}

// A held create stays queued. After the gap between new listings it goes up by itself: the listing's page, or the
// bulk run, sends it once the wait is over. After the day's last new listing nothing sends it later: saving the
// listing again once the wait is over does.
const HELD_BACK_SENTENCES = Object.freeze({
  paceWait: (name, wait) => `Voolist waits between new ${name} listings, so this one goes up by itself in about ${wait}.`,
  capReached: (name, wait) => `Voolist sends up to 10 new listings a day to ${name}. This one waits: save it again in about ${wait} to send it.`,
  marketplaceOff: (name) => `Voolist has paused sending new listings to ${name} for now. Nothing was sent: try again later.`,
});

// How long to wait before sending again a create held back for the gap between two new listings: the wait the
// marketplace asked for, a few seconds over so the next claim lands after it, and never longer than a gap any
// marketplace asks (Vestiaire's is two minutes).
const PACE_WAIT_MARGIN_MS = 3 * 1000;
const LONGEST_PACE_WAIT_MS = 150 * 1000;

/**
 * @param {(number|null)} retryAfterSeconds - the wait the marketplace asked for (createHeldBack's `retryAfter`)
 * @returns {number} milliseconds
 */
export const paceWaitMs = (retryAfterSeconds) =>
  Math.min((Number(retryAfterSeconds) > 0 ? Number(retryAfterSeconds) * 1000 : LONGEST_PACE_WAIT_MS) + PACE_WAIT_MARGIN_MS, LONGEST_PACE_WAIT_MS);

/**
 * A create the marketplace's claim held back for now, or null when the claim did not.
 *
 * Nothing was claimed or sent, and the create stays queued as it was: it is neither done nor failed, and
 * a later save, run or retry sends it. The claim says it with `alreadyClaimed` too, so a web app that
 * predates these reasons stops rather than creating.
 *
 * @param {Object} claim - the claim's answer
 * @param {string} platform
 * @returns {({success: false, heldBack: string, retryAfter: (number|null), error: string, platform: string}|null)}
 *   `retryAfter` in seconds, when the marketplace said
 */
export function createHeldBack(claim, platform) {
  const reason = HELD_BACK_REASONS.find((key) => claim?.[key] === true);
  if (!reason) return null;
  const retryAfter = Number(claim.retryAfter) > 0 ? Number(claim.retryAfter) : null;
  return {
    success: false,
    heldBack: reason,
    retryAfter,
    error: HELD_BACK_SENTENCES[reason](getPlatformDisplayName(platform), waitWords(retryAfter)),
    platform,
  };
}

// --------------------------------------------------------------------------------------
// What an attempt gets recorded as
// --------------------------------------------------------------------------------------

// The browser is the only place that knows how an attempt ended. Every way
// `publishOneExtensionListing` can finish maps to exactly one event here — a success or a failure,
// never both and never neither — so the two names together count every attempt and a publish
// success rate can be read off them. Counting only the failures gives a numerator with no
// denominator, which is what we had.

/**
 * The screens a browser publish can be started from. Kept as a closed set because the whole value of
 * the property is being able to say "this failure comes from the listing form, not the bulk run" —
 * a name that is merely plausible is worse than no name, since it will be believed. Anything not
 * listed here is recorded as 'unknown', so a caller that forgets, or misspells, shows up as a
 * countable bucket rather than as a screen that does not exist.
 *
 * These match the names the same screens already use for `useExtensionAvailability({ surface })`
 * (`listing-new-edit-form.jsx`, `delist-dialog.jsx`), so one breakdown reads across both events.
 */
export const PUBLISH_SURFACES = Object.freeze(['listing-form', 'bulk-cross-list', 'delist-relist']);

const asSurface = (surface) => (PUBLISH_SURFACES.includes(surface) ? surface : 'unknown');

/**
 * @param {string} listingId - the Voolist listing's UUID. The only id that exists on all three
 *   surfaces (this app, the extension, the server), so it is what joins an event to a record.
 * @param {{success:boolean, platform:string, error?:string, alreadyCreated?:boolean,
 *   timedOut?:boolean, unrecorded?:boolean, marketplaceUnconfirmed?:boolean}} result a terminal
 *   result of publishOneExtensionListing
 * @param {Object} [context]
 * @param {'extension'|'claim'|'publish'} [context.stage] - how far the attempt got before it failed.
 *   Ignored by a success.
 * @param {string} [context.surface] - the screen the publish was started from; one of PUBLISH_SURFACES.
 * @returns {{event:string, properties:Object}}
 */
export function publishOutcomeEvent(listingId, result, { stage = 'publish', surface } = {}) {
  if (result?.success) {
    return {
      event: ANALYTICS_EVENTS.CROSSLIST_PLATFORM_SUCCEEDED,
      properties: {
        listingId,
        platform: result.platform,
        // Which screen this publish was started from. Without it a failure cannot be attributed to
        // a flow, and a fix gets aimed at the wrong one — 110 Poshmark size failures were read as
        // bulk cross-list when the bulk path only ever sends listings that passed a size check.
        surface: asSurface(surface),
        // Nothing was posted: another tab or run had already claimed this create. Neither a new
        // success nor a failure, so it is marked rather than counted as either.
        alreadyCreated: Boolean(result.alreadyCreated),
      },
    };
  }

  return {
    event: ANALYTICS_EVENTS.CROSSLIST_PLATFORM_FAILED,
    properties: {
      listingId,
      platform: result?.platform,
      error: result?.error,
      surface: asSurface(surface),
      // Where it stopped. 'claim' means the marketplace was never asked — the create could not even
      // be booked with our own server — so these are our outages, not the marketplace's. Without it
      // the only way to tell the two apart is to match the error sentence, which is UI copy and will
      // be reworded one day, silently emptying whatever query relied on it.
      stage,
      // Why the post may be live although the create did not finish here, one flag per reason:
      // `timedOut` is the extension going quiet, `unrecorded` is our own recording failing, and
      // `marketplaceUnconfirmed` is the marketplace failing or never answering. Counted as
      // failures because the create did not finish, and kept apart from the failures we know
      // posted nothing: none of them is Poshmark or Depop turning us down, and a spike in them
      // must not be read as that.
      ...unconfirmedCreateFlags(result),
      // Held back for now by the marketplace's pace or daily limit, or a pause (createHeldBack): nothing
      // was sent and the create is still queued, so it is not a failure of the marketplace or of Voolist.
      ...(result?.heldBack ? { heldBack: result.heldBack } : {}),
    },
  };
}
